/**
 * A listagem de contas — serve tanto "A receber" quanto "A pagar".
 *
 * É UM componente para os dois lados de propósito. Receber e pagar têm a mesma
 * pergunta ("o que vence, o que atrasou, o que já foi quitado"), a mesma
 * mecânica de recorte e o mesmo pé de somas; o que muda é rótulo e o nome de
 * dois campos, e isso está resolvido em `plataforma/financeiro.ts`. Duas
 * cópias seriam duas telas divergindo — uma ganharia paginação e a outra não.
 *
 * Três decisões sustentam o desenho, as mesmas da listagem de notas fiscais:
 *
 *  · Os quatro KPIs RECORTAM a tabela em vez de só informar. "Atrasadas",
 *    "vence hoje", "vence na semana" e "quitadas no mês" são as quatro
 *    perguntas que alguém faz ao abrir o financeiro de manhã.
 *
 *  · Cor só onde alguém precisa agir hoje (spec 01). Atrasada é ERRO, vence
 *    hoje é ATENÇÃO, quitada é neutra e quieta. Verde em toda linha paga não
 *    classificaria nada — e vermelho em tudo ensina a ignorar vermelho.
 *
 *  · Filtro, contagem, soma e paginação são do SERVIDOR. O pé promete "soma do
 *    recorte"; somar em memória somaria a página e mentiria na segunda.
 *
 * O menu ⋮ oferece as quatro ações do contrato, e as duas que a regra proíbe
 * ficam DESABILITADAS com a razão escrita — nunca oferecidas para falhar
 * depois do clique. Quem decide é o SERVIDOR: `editavel`, `excluivel` e
 * `motivo_bloqueio` vêm prontos na linha, e a tela só os lê. Recalcular a
 * regra aqui seria ter duas cópias dela — e a segunda diverge na primeira
 * mudança, com o sintoma sendo justamente o botão que falha depois do clique.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LinkBotao } from '../../componentes/Botao'
import { CabecalhoDaGaveta, CorpoDaGaveta, Gaveta } from '../../componentes/Gaveta'
import { Icone } from '../../componentes/Icone'
import {
  CartaoKPI,
  Chips,
  MenuDeLinha,
  Paginacao,
  VazioFiltrado,
  VazioPrimeiraVez,
  type AcaoDeLinha,
} from '../../componentes/Listagem'
import { ErroApi } from '../../plataforma/api'
import { formatar, formatarMoeda } from '../../plataforma/dinheiro'
import {
  apoioDaSituacao,
  bloqueioDeEdicao,
  bloqueioDeExclusao,
  carregarContas,
  intervaloDoMes,
  mesesRecentes,
  statusBaixado,
  urgenciaDaConta,
  type Lado,
  type LinhaConta,
  type ListaDeContas as Lista,
  type RecorteDeContas,
  type SituacaoConta,
} from '../../plataforma/financeiro'
import { dataISOparaBR } from '../../plataforma/mascaras'
import { DialogoDeBaixa } from './DialogoDeBaixa'
import { DialogoDeExclusao } from './DialogoDeExclusao'
import { FichaDaConta } from './FichaDaConta'
import { SeloDaConta } from './SeloDaConta'

const TEXTO = {
  receber: {
    substantivo: 'conta a receber',
    plural: 'contas a receber',
    colunaPessoa: 'Cliente',
    chipBaixada: 'Recebidas',
    kpiBaixado: 'Recebido no mês',
    somaBaixado: 'Recebido',
    acao: 'Receber…',
    novo: 'Novo recebimento',
    buscar: 'Buscar documento, cliente ou descrição',
    vazioTitulo: 'Nenhuma conta a receber',
    vazioTexto:
      'A conta a receber nasce da venda a prazo — crediário e cartão parcelado geram uma parcela cada. O que não vem de documento do sistema entra por lançamento manual.',
  },
  pagar: {
    substantivo: 'conta a pagar',
    plural: 'contas a pagar',
    colunaPessoa: 'Fornecedor',
    chipBaixada: 'Pagas',
    kpiBaixado: 'Pago no mês',
    somaBaixado: 'Pago',
    acao: 'Pagar…',
    novo: 'Nova conta',
    buscar: 'Buscar documento, fornecedor ou descrição',
    vazioTitulo: 'Nenhuma conta a pagar',
    vazioTexto:
      'A conta a pagar nasce da nota fiscal de compra. O que não chega por documento — aluguel, energia, folha — entra por lançamento manual.',
  },
} as const

/**
 * O que a gaveta está mostrando.
 *
 * Só `ver`: lançar e editar viraram TELA (`TelaLancamento.tsx`). O formulário
 * tem doze campos, e a gaveta fecha no Esc e no clique no fundo sem
 * confirmação — o que é conveniência numa ficha de leitura vira perda de
 * trabalho num formulário longo.
 */
type Gavetinha = { modo: 'ver'; linha: LinhaConta } | null

/** O lado viaja na busca para a tela de lançamento abrir no tipo certo. */
function caminhoDeNovo(lado: Lado) {
  return `/app/financeiro/lancamentos/novo?lado=${lado}`
}

function caminhoDeEdicao(lado: Lado, numeroDocumento: string) {
  return `/app/financeiro/lancamentos/${encodeURIComponent(numeroDocumento)}/editar?lado=${lado}`
}

export function ListaDeContas({
  lado,
  recorteInicial = '',
}: {
  lado: Lado
  /** Recorte com que a aba abre, quando veio de um KPI da visão geral. */
  recorteInicial?: RecorteDeContas | ''
}) {
  const navegar = useNavigate()
  const texto = TEXTO[lado]
  const meses = useMemo(() => mesesRecentes(), [])

  const [mes, setMes] = useState('')
  const [situacao, setSituacao] = useState<SituacaoConta | ''>('')
  const [recorte, setRecorte] = useState<RecorteDeContas | ''>(recorteInicial)
  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')
  const [pagina, setPagina] = useState(1)

  const [lista, setLista] = useState<Lista | null>(null)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [versao, setVersao] = useState(0)

  const [gaveta, setGaveta] = useState<Gavetinha>(null)
  const [baixando, setBaixando] = useState<LinhaConta | null>(null)
  const [excluindo, setExcluindo] = useState<LinhaConta | null>(null)

  // Debounce da busca: cada tecla disparando uma consulta que agrega e conta o
  // período inteiro é como se derruba o banco em dia de movimento.
  useEffect(() => {
    const relogio = setTimeout(() => {
      setBuscaAtiva(busca)
      setPagina(1)
    }, 300)
    return () => clearTimeout(relogio)
  }, [busca])

  const periodo = useMemo(() => (mes ? intervaloDoMes(mes) : null), [mes])

  useEffect(() => {
    const controle = new AbortController()
    carregarContas(
      lado,
      {
        de: periodo?.de,
        ate: periodo?.ate,
        situacao,
        recorte,
        busca: buscaAtiva,
        pagina,
      },
      controle.signal,
    )
      .then((dados) => {
        setLista(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(
          causa instanceof ErroApi
            ? causa.message
            : `Não foi possível carregar as ${texto.plural}.`,
        )
      })
    return () => controle.abort()
  }, [lado, periodo, situacao, recorte, buscaAtiva, pagina, versao, texto.plural])

  const contagens = lista?.contagens
  const recortado = !!buscaAtiva || !!situacao || !!recorte

  function limparRecorte() {
    setBusca('')
    setBuscaAtiva('')
    setSituacao('')
    setRecorte('')
    setPagina(1)
  }

  function alternarRecorte(alvo: RecorteDeContas) {
    setRecorte((atual) => (atual === alvo ? '' : alvo))
    setPagina(1)
  }

  /** Fecha o que estiver aberto, avisa e recarrega — a lista mudou. */
  function concluir(mensagem: string) {
    setGaveta(null)
    setBaixando(null)
    setExcluindo(null)
    setAviso(mensagem)
    setVersao((n) => n + 1)
  }

  const rotuloDoPeriodo = mes
    ? (meses.find((m) => m.valor === mes)?.rotulo ?? mes)
    : 'todo o período'

  return (
    <div className="flex w-full flex-col gap-4">
      {erro && (
        <div
          role="alert"
          className="flex items-center gap-3 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto"
        >
          <Icone nome="error" tamanho={18} className="shrink-0" />
          <span className="min-w-0 flex-1">{erro}</span>
          <button
            type="button"
            onClick={() => setVersao((n) => n + 1)}
            className="h-8 shrink-0 rounded-sistema border border-erro bg-superficie px-3 text-[12.5px] font-semibold text-erro"
          >
            Tentar de novo
          </button>
        </div>
      )}

      {aviso && (
        <div
          role="status"
          className="flex items-center gap-3 rounded-sistema bg-ok-suave px-4 py-3 text-sm text-ok-texto"
        >
          <Icone nome="check_circle" tamanho={18} className="shrink-0" />
          <span className="min-w-0 flex-1">{aviso}</span>
          <button
            type="button"
            onClick={() => setAviso('')}
            className="shrink-0 text-[12.5px] font-semibold underline-offset-2 hover:underline"
          >
            Fechar
          </button>
        </div>
      )}

      {/* Quatro recortes, todos clicáveis, todos um subconjunto de linhas. */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <CartaoKPI
          icone="warning"
          tom={contagens && contagens.atrasada > 0 ? 'erro' : 'neutro'}
          rotulo="Atrasadas"
          valor={formatarMoeda(contagens?.atrasado_centavos ?? 0)}
          apoio={
            contagens && contagens.atrasada > 0
              ? `${contagens.atrasada} ${contagens.atrasada === 1 ? 'conta venceu' : 'contas venceram'} e não ${contagens.atrasada === 1 ? 'foi baixada' : 'foram baixadas'}`
              : 'nada vencido em aberto'
          }
          ativo={recorte === 'atrasadas'}
          aoClicar={() => alternarRecorte('atrasadas')}
        />
        <CartaoKPI
          icone="calendario"
          tom={contagens && contagens.vencem_hoje > 0 ? 'atencao' : 'neutro'}
          rotulo="Vence hoje"
          valor={formatarMoeda(contagens?.vence_hoje_centavos ?? 0)}
          apoio={
            contagens && contagens.vencem_hoje > 0
              ? `${contagens.vencem_hoje} ${contagens.vencem_hoje === 1 ? 'conta' : 'contas'} · é o trabalho de hoje`
              : 'nada vence hoje'
          }
          ativo={recorte === 'vence_hoje'}
          aoClicar={() => alternarRecorte('vence_hoje')}
        />
        {/* Sem cor: a semana é aviso, não trabalho de hoje. */}
        <CartaoKPI
          icone="atraso"
          rotulo="Vence na semana"
          valor={formatarMoeda(contagens?.vence_na_semana_centavos ?? 0)}
          apoio={
            contagens && contagens.vencem_na_semana > 0
              ? `${contagens.vencem_na_semana} ${contagens.vencem_na_semana === 1 ? 'conta' : 'contas'} nos próximos 7 dias`
              : 'nada nos próximos 7 dias'
          }
          ativo={recorte === 'vence_na_semana'}
          aoClicar={() => alternarRecorte('vence_na_semana')}
        />
        {/* Quitada é desfecho normal: fica neutra e quieta. */}
        <CartaoKPI
          icone="check_circle"
          rotulo={texto.kpiBaixado}
          valor={formatarMoeda(contagens?.baixado_no_mes_centavos ?? 0)}
          apoio={
            contagens && contagens.baixadas_no_mes > 0
              ? `${contagens.baixadas_no_mes} ${contagens.baixadas_no_mes === 1 ? 'conta baixada' : 'contas baixadas'} neste mês`
              : 'nenhuma baixa neste mês'
          }
          ativo={recorte === 'baixadas_no_mes'}
          aoClicar={() => alternarRecorte('baixadas_no_mes')}
        />
      </div>

      {/* A barra fica SEMPRE, mesmo com a lista vazia: foi um filtro que
          produziu o vazio, e esconder o controle que causou o vazio é beco sem
          saída, não economia de tela. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Chips
          rotuloDoGrupo={`Situação das ${texto.plural}`}
          valor={situacao}
          aoTrocar={(chave) => {
            setSituacao(chave as SituacaoConta | '')
            setPagina(1)
          }}
          chips={[
            { chave: '', rotulo: 'Todas', contagem: contagens?.total, sempre: true },
            { chave: 'aberta', rotulo: 'Em aberto', contagem: contagens?.aberta },
            { chave: 'atrasada', rotulo: 'Atrasadas', contagem: contagens?.atrasada },
            {
              chave: statusBaixado(lado),
              rotulo: texto.chipBaixada,
              contagem: contagens?.baixada,
            },
            { chave: 'cancelada', rotulo: 'Canceladas', contagem: contagens?.cancelada },
          ]}
        />

        <div className="flex flex-wrap items-center gap-2.5">
          <label className="sr-only" htmlFor={`periodo-${lado}`}>
            Período de vencimento
          </label>
          <select
            id={`periodo-${lado}`}
            value={mes}
            onChange={(evento) => {
              setMes(evento.target.value)
              setPagina(1)
            }}
            className="campo h-[34px] w-auto max-w-[190px] px-3 text-[13px] font-semibold capitalize"
          >
            <option value="">Todo o período</option>
            {meses.map((item) => (
              <option key={item.valor} value={item.valor} className="capitalize">
                {item.rotulo}
              </option>
            ))}
          </select>

          <label className="composto h-[34px] w-[280px] min-w-0 cursor-text">
            <Icone nome="search" tamanho={17} className="text-texto-fraco" />
            <input
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder={texto.buscar}
            />
            <span className="sr-only">Buscar {texto.plural}</span>
          </label>

          <LinkBotao para={caminhoDeNovo(lado)} icone="add">
            {texto.novo}
          </LinkBotao>
        </div>
      </div>

      {!lista ? (
        <div className="h-[320px] animate-pulse rounded-sistema bg-superficie" />
      ) : (
        <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
          {lista.linhas.length === 0 ? (
            recortado ? (
              <VazioFiltrado
                titulo={buscaAtiva ? `Nada para “${buscaAtiva}”` : 'Nenhuma conta neste recorte'}
                descricao={`Existem ${lista.contagens.total} ${lista.contagens.total === 1 ? texto.substantivo : texto.plural} em ${rotuloDoPeriodo}. Nenhuma delas combina com esse recorte.`}
                aoLimpar={limparRecorte}
              />
            ) : (
              <VazioPrimeiraVez
                icone="payments"
                titulo={mes ? `Nenhuma conta em ${rotuloDoPeriodo}` : texto.vazioTitulo}
                descricao={texto.vazioTexto}
                acao={
                  <LinkBotao para={caminhoDeNovo(lado)} icone="add">
                    {texto.novo}
                  </LinkBotao>
                }
              />
            )
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] border-collapse">
                  <caption className="sr-only">
                    {texto.plural} em {rotuloDoPeriodo}
                  </caption>
                  <thead>
                    <tr className="bg-fundo">
                      {[
                        { rotulo: 'Documento', alinhamento: 'text-left' },
                        { rotulo: texto.colunaPessoa, alinhamento: 'text-left' },
                        { rotulo: 'Emissão', alinhamento: 'text-left' },
                        { rotulo: 'Vencimento', alinhamento: 'text-left' },
                        { rotulo: 'Situação', alinhamento: 'text-left' },
                        { rotulo: 'Valor', alinhamento: 'text-right' },
                        { rotulo: '', alinhamento: 'text-right' },
                      ].map((coluna) => (
                        <th
                          key={coluna.rotulo || 'acoes'}
                          scope="col"
                          className={`border-b border-borda px-3 py-2.5 text-[12.5px] font-semibold text-texto-suave first:pl-4 last:pr-4 ${coluna.alinhamento}`}
                        >
                          {coluna.rotulo}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lista.linhas.map((linha, i) => (
                      <LinhaDaTabela
                        key={linha.codigo}
                        lado={lado}
                        linha={linha}
                        ultima={i === lista.linhas.length - 1}
                        aberta={gaveta?.modo === 'ver' && gaveta.linha.codigo === linha.codigo}
                        aoVer={() => setGaveta({ modo: 'ver', linha })}
                        aoBaixar={() => setBaixando(linha)}
                        aoEditar={() => navegar(caminhoDeEdicao(lado, linha.numero_documento))}
                        aoExcluir={() => setExcluindo(linha)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <RodapeDeSomas lado={lado} lista={lista} />

              <Paginacao
                pagina={lista.pagina}
                paginas={lista.paginas}
                total={lista.total}
                tamanho={lista.tamanho}
                substantivo="conta"
                aoTrocarPagina={setPagina}
              />
            </>
          )}
        </div>
      )}

      {/*
        A ficha continua em GAVETA: é leitura de um título, e o contexto da
        linha atrás importa. O FORMULÁRIO saiu — virou tela com endereço
        próprio (`TelaLancamento.tsx`), porque doze campos atrás de um Esc que
        descarta sem aviso é armadilha, não conveniência.
      */}
      {gaveta?.modo === 'ver' && (
        <Gaveta
          rotulo={`Lançamento ${gaveta.linha.numero_documento}`}
          largura="formulario"
          aoFechar={() => setGaveta(null)}
        >
          {/* A DESCRIÇÃO é o título, e o número é o subtítulo — não o
              contrário. Quem abre a gaveta reconhece o título pelo que ele é
              ("Pedido de atacado B-84"), e confere o número depois. */}
          <CabecalhoDaGaveta
            titulo={gaveta.linha.descricao}
            subtitulo={`${gaveta.linha.numero_documento} · ${gaveta.linha.codigo}`}
            rotuloFechar="Fechar lançamento"
            aoFechar={() => setGaveta(null)}
          />
          <CorpoDaGaveta>
            <FichaDaConta
              lado={lado}
              linha={gaveta.linha}
              aoFechar={() => setGaveta(null)}
              aoBaixar={() => {
                setBaixando(gaveta.linha)
                setGaveta(null)
              }}
              aoEditar={() => navegar(caminhoDeEdicao(lado, gaveta.linha.numero_documento))}
            />
          </CorpoDaGaveta>
        </Gaveta>
      )}

      {baixando && (
        <DialogoDeBaixa
          lado={lado}
          linha={baixando}
          aoFechar={() => setBaixando(null)}
          aoConcluir={concluir}
        />
      )}

      {excluindo && (
        <DialogoDeExclusao
          lado={lado}
          linha={excluindo}
          aoFechar={() => setExcluindo(null)}
          aoConcluir={concluir}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ linha */

function LinhaDaTabela({
  lado,
  linha,
  ultima,
  aberta,
  aoVer,
  aoBaixar,
  aoEditar,
  aoExcluir,
}: {
  lado: Lado
  linha: LinhaConta
  ultima: boolean
  aberta: boolean
  aoVer: () => void
  aoBaixar: () => void
  aoEditar: () => void
  aoExcluir: () => void
}) {
  const texto = TEXTO[lado]
  const borda = ultima ? '' : 'border-b border-divisoria'
  const urgencia = urgenciaDaConta(linha)
  const pessoa = lado === 'receber' ? linha.cliente : linha.fornecedor
  const quitada = urgencia === 'quitada'

  const acoes: AcaoDeLinha[] = [
    { rotulo: 'Ver lançamento', icone: 'visibility', aoEscolher: aoVer },
    {
      rotulo: texto.acao,
      icone: 'payments',
      aoEscolher: aoBaixar,
      razaoImpedida:
        urgencia === 'cancelada'
          ? 'título cancelado'
          : quitada
            ? `já ${lado === 'receber' ? 'recebido' : 'pago'}${linha.baixada_em ? ` em ${dataISOparaBR(linha.baixada_em)}` : ''}`
            : undefined,
    },
    {
      rotulo: 'Editar',
      icone: 'edit_note',
      aoEscolher: aoEditar,
      separar: true,
      razaoImpedida: bloqueioDeEdicao(linha),
    },
    {
      rotulo: 'Excluir…',
      icone: 'close',
      aoEscolher: aoExcluir,
      destrutiva: true,
      razaoImpedida: bloqueioDeExclusao(linha),
    },
  ]

  return (
    <tr className={aberta ? 'bg-marca-fundo' : 'hover:bg-superficie-suave'}>
      {/* Documento: a identidade que o lojista imprime e dita. O código
          interno (R0406) fica na gaveta — dois números na mesma célula
          competem, e é o documento que ele procura no papel. */}
      <td className={`px-3 py-2.5 pl-4 ${borda}`}>
        <button
          type="button"
          onClick={aoVer}
          className="tabular text-left text-[13.5px] font-semibold text-tinta hover:text-marca"
        >
          {linha.numero_documento}
        </button>
        <div className="mt-0.5 max-w-[240px] truncate text-xs text-texto-suave">
          {linha.descricao}
          {linha.total_parcelas > 1 && (
            <span className="tabular">
              {' '}
              · {linha.numero_parcela}/{linha.total_parcelas}
            </span>
          )}
        </div>
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        {pessoa ? (
          <>
            <div className="text-[13.5px] font-semibold text-tinta">{pessoa.nome}</div>
            <div className="tabular mt-0.5 text-xs text-texto-suave">{pessoa.codigo}</div>
          </>
        ) : (
          <span className="text-[13px] text-texto-fraco">—</span>
        )}
      </td>

      <td className={`tabular px-3 py-2.5 text-[13px] text-texto ${borda}`}>
        {dataISOparaBR(linha.emissao)}
      </td>

      {/* Vencimento vencido em vermelho e em negrito: é o dado que decide se
          alguém liga para o cliente hoje. */}
      <td
        className={`tabular px-3 py-2.5 text-[13px] ${borda} ${
          urgencia === 'atrasada' ? 'font-bold text-erro' : 'text-texto'
        }`}
      >
        {dataISOparaBR(linha.vencimento)}
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <SeloDaConta lado={lado} linha={linha} />
        <span className="mt-0.5 block text-[11.5px] text-texto-suave">
          {apoioDaSituacao(linha, lado)}
        </span>
      </td>

      <td
        className={`tabular px-3 py-2.5 text-right text-[13px] ${borda} ${
          urgencia === 'cancelada'
            ? 'text-texto-suave line-through'
            : 'font-semibold text-tinta'
        }`}
      >
        {formatarMoeda(linha.valor_centavos)}
        {linha.valor_baixado_centavos !== undefined &&
          linha.valor_baixado_centavos !== linha.valor_centavos && (
            <div className="mt-0.5 text-[11.5px] font-normal text-texto-suave">
              baixado {formatar(linha.valor_baixado_centavos)}
            </div>
          )}
      </td>

      <td className={`px-3 py-2.5 pr-4 ${borda}`}>
        <MenuDeLinha rotulo={`Ações de ${linha.numero_documento}`} acoes={acoes} />
      </td>
    </tr>
  )
}

/* ----------------------------------------------------------------- somas */

/**
 * O pé soma o RECORTE, não a página — é o que o filtro prometeu.
 *
 * As três parcelas explicam o total: o que ainda está no prazo, o que já
 * venceu e o que já entrou (ou saiu). Um total sozinho não diria em que pé
 * está a cobrança.
 */
function RodapeDeSomas({ lado, lista }: { lado: Lado; lista: Lista }) {
  const { somas, total, contagens } = lista
  const texto = TEXTO[lado]

  const parcelas: { rotulo: string; valor: number; cor: string }[] = [
    { rotulo: 'Total', valor: somas.total_centavos, cor: 'text-tinta' },
    { rotulo: 'Em aberto', valor: somas.aberto_centavos, cor: 'text-tinta' },
    { rotulo: 'Atrasado', valor: somas.atrasado_centavos, cor: 'text-erro' },
    { rotulo: texto.somaBaixado, valor: somas.baixado_centavos, cor: 'text-texto-suave' },
  ]

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-t border-borda bg-fundo px-4 py-3">
      <span className="text-[13px] text-texto-suave">
        Soma do recorte · <span className="tabular">{total}</span>{' '}
        {total === 1 ? 'conta' : 'contas'}
        {contagens.cancelada > 0 && ', canceladas fora'}
      </span>
      <div className="flex flex-wrap gap-x-7 gap-y-2">
        {parcelas.map((soma) => (
          <div key={soma.rotulo}>
            <div className="text-[11px] font-bold tracking-[0.6px] text-texto-suave uppercase">
              {soma.rotulo}
            </div>
            <div className={`tabular text-[14px] font-semibold ${soma.cor}`}>
              {formatar(soma.valor)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
